import {
  getCheckoutFunnelType,
  initiateLegacyFacebookCheckout,
  sendCheckoutEventPageView,
} from "./pageViewHelper";
import { addPaymentInfo } from "../Payment/paymentHelpers";
import { isLoggedIn } from "../apiHelpers";
import { getCartAndSendShippingEvent } from "~/Gtm/getCartAndSendEvent";
import Store from "~/common/Store";
import { updateUser } from "~/common/Store/actions/user";
import { sendBingViewCartEvent } from "~/bingAnalytics";
import { createProducts, formatDefaultProducts } from "../productHelpers";
import { Item } from "~/userAnalytics";
import { formatEcommerceData } from "../AddToCart/addToCartHelpers";
import { sendGtmEvent } from "../definitions";

type PageInfo = {
  environment: string;
  page_type: string;
  page_value: string;
  clean_url: string;
  page_category: string;
  search_value: string;
  page_extra_info: string;
};

/**
 * Handles the on load checkout event. Only called once on the page
 * @param items array of item numbers
 */
export const handleViewCartPage = async (items: Item[]) => {
  const products = await createProducts(items);
  const funnelType = getCheckoutFunnelType();
  sendCheckoutEventPageView("Cart", funnelType, products);
};

/**
 * Calls the begin_checkout_and_view_cart event on load and updates any time the cart is updated (such as incrementing quantity on cart page)
 * @param items array of item numbers
 * @param totalValue total value of the cart
 * @param coupon all coupon codes concatenated into a single string
 */
export const handleViewCartPageAndUpdates = async (
  items: Item[],
  totalValue: number,
  coupons: string,
) => {
  const baseProductsData = await createProducts(items, true);
  const products = formatDefaultProducts(baseProductsData);
  const productsFeedIds = [];
  for (const product of products) {
    product.position = products.indexOf(product);
    productsFeedIds.push(product.id);
  }
  const { user } = Store.getState();
  user.analyticsData.cart_total = totalValue;
  updateUser(user);

  sendBingViewCartEvent(productsFeedIds);
  const formattedEcommerceData = formatEcommerceData(products, {
    coupons,
    total: totalValue,
  });
  sendGtmEvent({
    event: "begin_checkout_and_view_cart",
    ecommerce: formattedEcommerceData,
  });
};

const handleViewInfoPageView = () => {
  sendCheckoutEventPageView("ReviewPayment", getCheckoutFunnelType());
  initiateLegacyFacebookCheckout();

  // TODO - These event listeners will be rewritten in follow up tickets to be on the actual elements instead of here
  const ccField = document.getElementById("card_number");
  if (ccField !== null) {
    ccField.addEventListener("focusout", () => {
      let ccType: Element | string | null = document.querySelector(
        "ul.payment > li.active",
      );
      if (ccType === null) {
        ccType = "credit_card";
      } else {
        ccType = ccType.innerHTML;
      }
      addPaymentInfo(ccType, false);
    });
  }

  const savedccField = document.querySelector(
    "select[name=storedCCPayment]",
  ) as HTMLInputElement;
  if (savedccField !== null) {
    savedccField.addEventListener("change", () => {
      if (savedccField.value !== "") {
        addPaymentInfo("credit_card", false);
      }
    });
  }

  const altPaymentCheckbox = document.querySelector(
    "input[name=altpayment_confirm]",
  ) as HTMLInputElement;
  if (altPaymentCheckbox !== null) {
    altPaymentCheckbox.addEventListener("change", () => {
      if (altPaymentCheckbox.checked) {
        addPaymentInfo("altpayment", false);
      }
    });
  }

  if (isLoggedIn() === true) {
    getCartAndSendShippingEvent();
  }
};

const handleShippingBillingInfoPageView = () => {
  sendCheckoutEventPageView("BillingPage", getCheckoutFunnelType());
  initiateLegacyFacebookCheckout();
};

export const handlePageView = () => {
  // Most pages have the defined GoogleTagManagerMeta property defined in their cshtml file, but not all.
  // PageInfo will be the first event defined in the data layer (if defined) as it is pushed in on _LayoutCore.cshtml, which is loaded for all pages.

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore-next-line
  if (!window.dataLayer[0]?.page_type) return;

  const pageInfo = window.dataLayer[0] as PageInfo;
  if (pageInfo.page_type === "/shipping-billinginfo.cfm") {
    handleShippingBillingInfoPageView();
  }
  if (pageInfo.page_type === "viewinfo.cfm") {
    handleViewInfoPageView();
  }
};
